import { useState } from 'react';
import { useStore } from '../state/store';
import { Heading, panelStyle } from './ui';

/**
 * Token entry.
 *
 * The console is a static bundle with no server session of its own, so the
 * bridge's bearer token is the whole of its authentication. It is verified
 * against `/system` before being stored, and stored only for this tab unless
 * the reader deliberately opts into keeping it on the device.
 */
export function ConnectGate() {
  const signIn = useStore((s) => s.signIn);
  const signingIn = useStore((s) => s.signingIn);
  const authError = useStore((s) => s.authError);
  const theme = useStore((s) => s.theme);
  const setTheme = useStore((s) => s.setTheme);

  const [token, setToken] = useState('');
  const [remember, setRemember] = useState(false);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--page)',
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (token.trim()) void signIn(token.trim(), remember ? 'device' : 'session');
        }}
        style={{
          ...panelStyle,
          width: 'min(420px, 100%)',
          padding: '28px 28px 24px',
          animation: 'fadeIn 200ms ease-out',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 11,
            marginBottom: 20,
          }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              flex: 'none',
              borderRadius: 9,
              background: 'var(--ink)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 21 21"
              fill="none"
              style={{ color: 'var(--panel)' }}
            >
              <rect
                x="2"
                y="2"
                width="7"
                height="7"
                rx="1.5"
                stroke="currentColor"
                strokeWidth="1.9"
              />
              <rect
                x="12"
                y="2"
                width="7"
                height="7"
                rx="1.5"
                stroke="currentColor"
                strokeWidth="1.9"
              />
              <rect
                x="2"
                y="12"
                width="7"
                height="7"
                rx="1.5"
                stroke="currentColor"
                strokeWidth="1.9"
              />
              <rect
                x="12"
                y="12"
                width="7"
                height="7"
                rx="1.5"
                stroke="currentColor"
                strokeWidth="1.9"
              />
            </svg>
          </div>
          <div>
            <Heading size={16} style={{ letterSpacing: '-0.01em' }}>
              PM2 Console
            </Heading>
            <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 1 }}>
              {window.location.host}
            </div>
          </div>
        </div>

        <label
          htmlFor="token"
          style={{
            display: 'block',
            fontSize: 13,
            color: 'var(--muted)',
            marginBottom: 8,
            lineHeight: 1.55,
            textWrap: 'pretty',
          }}
        >
          Paste the bridge token. It is printed by{' '}
          <code style={{ fontSize: 12.5 }}>npm run pair</code> on the host, and stored in{' '}
          <code style={{ fontSize: 12.5 }}>packages/.env</code>.
        </label>

        <input
          id="token"
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          autoComplete="current-password"
          autoFocus
          spellCheck={false}
          placeholder="43-character token"
          style={{
            width: '100%',
            padding: '11px 13px',
            borderRadius: 11,
            border: `1px solid ${authError ? 'var(--neg)' : 'var(--line)'}`,
            background: 'var(--page)',
            color: 'var(--ink)',
            fontSize: 14,
            fontVariantNumeric: 'tabular-nums',
          }}
        />

        {authError ? (
          <div
            style={{
              fontSize: 12.5,
              color: 'var(--neg)',
              marginTop: 8,
              lineHeight: 1.5,
            }}
          >
            {authError}
          </div>
        ) : null}

        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 9,
            marginTop: 16,
            fontSize: 12.5,
            color: 'var(--muted)',
            cursor: 'pointer',
            lineHeight: 1.5,
          }}
        >
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            style={{ marginTop: 2, accentColor: 'var(--pos)' }}
          />
          <span>
            Keep on this device. Otherwise the token is forgotten when this tab closes —
            it grants read access to every process on the host.
          </span>
        </label>

        <button
          type="submit"
          disabled={signingIn || token.trim().length === 0}
          style={{
            width: '100%',
            marginTop: 18,
            padding: '11px 16px',
            borderRadius: 999,
            background: 'var(--ink)',
            color: 'var(--panel)',
            fontSize: 14,
            fontWeight: 600,
            opacity: signingIn || token.trim().length === 0 ? 0.45 : 1,
            cursor: signingIn || token.trim().length === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {signingIn ? 'Checking…' : 'Connect'}
        </button>

        <div
          style={{
            display: 'flex',
            gap: 4,
            padding: 3,
            borderRadius: 999,
            background: 'var(--page)',
            marginTop: 18,
          }}
        >
          {(['light', 'dark'] as const).map((mode) => {
            const on = theme === mode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => setTheme(mode)}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  borderRadius: 999,
                  fontSize: 12.5,
                  textAlign: 'center',
                  background: on ? 'var(--panel)' : 'transparent',
                  color: on ? 'var(--ink)' : 'var(--muted)',
                  boxShadow: on ? 'var(--card)' : 'none',
                  textTransform: 'capitalize',
                }}
              >
                {mode}
              </button>
            );
          })}
        </div>
      </form>
    </div>
  );
}
